import { ArrowUpDown, Loader2, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { formatCurrency, units } from "../../lib/utils";

export function MergeDialog({
  open, onOpenChange, mergePair, mergeNormalized,
  mergeForm, setMergeForm, onMerge, saving,
}) {
  if (!mergePair) return null;

  const totalStock = mergeNormalized
    ? mergeNormalized.stockA + mergeNormalized.stockB
    : mergePair[0].current_stock + mergePair[1].current_stock;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpDown className="w-5 h-5 text-primary" /> Fusionar ingredientes
          </DialogTitle>
          <DialogDescription>El stock se sumara automaticamente. Edita los valores del ingrediente resultante.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg text-sm">
          {mergePair.map((ing, idx) => {
            const normStock = idx === 0 ? mergeNormalized?.stockA : mergeNormalized?.stockB;
            const converted = mergeNormalized && ing.unit !== mergeNormalized.unit;
            return (
              <div key={ing.ingredient_id} className="space-y-1">
                <p className="font-semibold text-xs uppercase text-muted-foreground tracking-wide">{idx === 0 ? "Ingrediente A" : "Ingrediente B"}</p>
                <p className="font-medium truncate">{ing.name}</p>
                <p className="text-muted-foreground">
                  Stock: <strong>{ing.current_stock} {ing.unit}</strong>
                  {converted && normStock !== undefined && <span className="ml-1 text-primary text-xs">→ {normStock.toFixed(3)} {mergeNormalized.unit}</span>}
                </p>
                <p className="text-muted-foreground">Coste: {formatCurrency(ing.cost_per_unit)}/{ing.unit}</p>
                <p className="text-muted-foreground">Merma: {ing.waste_percentage}%</p>
              </div>
            );
          })}
        </div>

        {mergeNormalized?.note && (
          <div className={`text-xs px-3 py-2 rounded-lg ${mergeNormalized.note.includes("incompatibles") ? "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400" : "bg-primary/10 text-primary"}`}>
            {mergeNormalized.note}
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-primary"><Check className="w-4 h-4" /> Resultado de la fusion</div>
          <div className="space-y-2">
            <Label>Nombre</Label>
            <Input value={mergeForm.name || ""} onChange={e => setMergeForm(f => ({ ...f, name: e.target.value }))} data-testid="merge-name-input" />
            <div className="flex gap-1">
              {mergePair.map((ing, idx) => (
                <button key={idx} type="button" onClick={() => setMergeForm(f => ({ ...f, name: ing.name }))}
                  className="text-xs px-2 py-0.5 rounded border hover:bg-muted transition-colors">Usar "{ing.name}"</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Unidad</Label>
              <Select value={mergeForm.unit || "kg"} onValueChange={v => setMergeForm(f => ({ ...f, unit: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{units.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Stock resultante</Label>
              <Input value={`${totalStock.toFixed(3).replace(/\.?0+$/, '')} ${mergeForm.unit || ""}`} readOnly className="bg-muted font-mono text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Coste por unidad (EUR)</Label>
              <Input type="number" step="0.0001" value={mergeForm.cost_per_unit || ""} onChange={e => setMergeForm(f => ({ ...f, cost_per_unit: e.target.value }))} />
              <div className="flex gap-1">
                {mergePair.map((ing, idx) => (
                  <button key={idx} type="button" onClick={() => setMergeForm(f => ({ ...f, cost_per_unit: ing.cost_per_unit.toString() }))}
                    className="text-xs px-1.5 py-0.5 rounded border hover:bg-muted transition-colors">{formatCurrency(ing.cost_per_unit)}</button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>% Merma</Label>
              <Input type="number" step="0.1" min="0" max="100" value={mergeForm.waste_percentage || ""} onChange={e => setMergeForm(f => ({ ...f, waste_percentage: e.target.value }))} />
              <div className="flex gap-1">
                {mergePair.map((ing, idx) => (
                  <button key={idx} type="button" onClick={() => setMergeForm(f => ({ ...f, waste_percentage: ing.waste_percentage.toString() }))}
                    className="text-xs px-1.5 py-0.5 rounded border hover:bg-muted transition-colors">{ing.waste_percentage}%</button>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Stock minimo</Label>
            <Input type="number" step="0.01" value={mergeForm.min_stock || ""} onChange={e => setMergeForm(f => ({ ...f, min_stock: e.target.value }))} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={onMerge} disabled={saving || !mergeForm.name?.trim()} data-testid="confirm-merge-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} Fusionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
